import { AxiosResponse } from 'axios';
import axiosInstance from './axios';

/**
 * FSC label admin. Base path /market_indicators/fsc_labels, internal_tool:admin only.
 *
 * Apple requires nature-based projects to be FSC certified. Creating, updating or
 * deleting a label changes the project's Apple Spec status in Supply Bridge immediately.
 * The label is internal only and must not be surfaced anywhere customer-facing.
 *
 * Every success is HTTP 200 as { message, response }; these helpers unwrap `response`.
 */
export type FscScope = 'required' | 'tagged';
export type FscStatus = 'pending' | 'certified' | 'not_certified';

export type FscLabelRow = {
  /** Label id. Null until a label exists; use it for update and delete. */
  id: string | null;
  /** Project uuid; use it for create. */
  project_id: string;
  registry_project_id: string;
  ghg_standard_code: string;
  /** Registry code plus id, e.g. CAR1573. */
  project_key: string;
  project_name: string | null;
  /** project_type, methodology and existing_certificate are null for scope=tagged. */
  project_type: string | null;
  methodology: string | null;
  existing_certificate: string | null;
  fsc_status: FscStatus;
  is_fsc_certified: boolean | null;
  note: string | null;
  updated_by: string | null;
  updated_at: string | null;
};

export type FscLabelList = {
  scope: FscScope;
  page: number;
  limit: number;
  /** Total across all pages. */
  total_count: number;
  data: FscLabelRow[];
};

/** The stored label, as returned by create, update and delete. */
export type FscLabel = {
  id: string;
  project_id: string;
  is_fsc_certified: boolean;
  note: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export type FscLabelListParams = {
  scope?: FscScope;
  status?: FscStatus;
  search_text?: string;
  page?: number;
  limit?: number;
};

export type FscLabelCreatePayload = {
  project_id: string;
  is_fsc_certified?: boolean;
  note?: string;
};

export type FscLabelUpdatePayload = {
  is_fsc_certified?: boolean;
  /** null or "" clears the note. */
  note?: string | null;
};

/**
 * Carries the HTTP status so the screen can react to it: 409 when the project already
 * has a label, 404 when the label was removed by someone else, 400 for a project that
 * is unknown or is not the live copy.
 */
export class FscLabelApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'FscLabelApiError';
    this.status = status;
  }
}

function toFscError(error: any, action: string): FscLabelApiError {
  const body = error?.response?.data;
  // Nest's ValidationPipe reports a list of problems in `message`.
  const detail = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
  return new FscLabelApiError(
    `Error ${action}: ${detail || error?.message || 'internal service error'}`,
    error?.response?.status,
  );
}

const unwrap = <T>(response: AxiosResponse): T => response.data?.response;

const BASE_PATH = '/market_indicators/fsc_labels';

export const getFscLabels = async (params: FscLabelListParams = {}): Promise<FscLabelList> => {
  try {
    // Drop empty values so an untouched filter is not sent as `status=` or `search_text=`.
    const query = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );
    return unwrap<FscLabelList>(await axiosInstance.get(BASE_PATH, { params: query }));
  } catch (error) {
    throw toFscError(error, 'fetching FSC labels');
  }
};

export const createFscLabel = async (payload: FscLabelCreatePayload): Promise<FscLabel> => {
  try {
    return unwrap<FscLabel>(await axiosInstance.post(BASE_PATH, payload));
  } catch (error) {
    throw toFscError(error, 'creating FSC label');
  }
};

export const updateFscLabel = async (
  id: string,
  payload: FscLabelUpdatePayload,
): Promise<FscLabel> => {
  try {
    return unwrap<FscLabel>(await axiosInstance.put(`${BASE_PATH}/${id}`, payload));
  } catch (error) {
    throw toFscError(error, 'updating FSC label');
  }
};

export const deleteFscLabel = async (id: string): Promise<FscLabel> => {
  try {
    return unwrap<FscLabel>(await axiosInstance.delete(`${BASE_PATH}/${id}`));
  } catch (error) {
    throw toFscError(error, 'removing FSC label');
  }
};
